import { useState, Fragment } from "react";
import { SectionCard } from "./SectionCard";
import { AlertTriangle, ChevronRight, ChevronDown, CalendarClock } from "lucide-react";

const money = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Agrupa el desglose [{anio, estado, monto}] por año -> { Vencido, "Por vencer" }
function porAnio(desglose = []) {
  const map = {};
  for (const d of desglose) {
    map[d.anio] = map[d.anio] || { Vencido: 0, "Por vencer": 0 };
    map[d.anio][d.estado] = (map[d.anio][d.estado] || 0) + d.monto;
  }
  return Object.entries(map).sort(([a], [b]) => String(a).localeCompare(String(b)));
}

export default function ClientesRevision({ data }) {
  const [openRows, setOpenRows] = useState({});
  if (!data) return null;
  const items = (data.items || []).filter((r) => r.disponible_neto != null && r.disponible_neto < 0);
  const numCell = "px-2.5 py-2 text-right font-mono text-[13px] whitespace-nowrap tabular-nums";
  const numHead = "px-2.5 py-2.5 text-right font-semibold whitespace-nowrap";
  const toggle = (i) => setOpenRows((o) => ({ ...o, [i]: !o[i] }));

  return (
    <SectionCard
      id="clientes-revision"
      testid="section-clientes-revision"
      num="2.1"
      title="Clientes que necesitan revisión ante posible falta de Línea de Crédito"
      description="Listado automático de clientes cuyo Disponible Neto es negativo. Haz clic en el nombre del cliente para ver el detalle de vencimientos por año (vencido vs. por vencer) y decidir si conviene gestionar el cobro en lugar de ampliar la línea."
    >
      {items.length === 0 ? (
        <div
          data-testid="revision-empty"
          className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800"
        >
          <AlertTriangle className="h-4 w-4" />
          No hay clientes con Disponible Neto negativo. Todas las líneas cubren la exposición actual.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-red-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-red-50 text-red-700 text-[11px] uppercase tracking-wide">
                <th className="text-left px-3 py-2.5 font-semibold min-w-[240px] w-[34%]">Cliente</th>
                <th className={numHead}>Línea Total (USD)</th>
                <th className={numHead}>Fact. + Pend. (USD)</th>
                <th className={numHead}>Total Cartera (USD)</th>
                <th className={numHead}>Línea Disp. vs Fact. + Pend. (USD)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => {
                const grupos = porAnio(r.desglose);
                const open = !!openRows[i];
                return (
                  <Fragment key={`grp-${i}`}>
                    <tr
                      key={`row-${i}`}
                      data-testid="revision-row"
                      className="border-b border-[#f3d6d6] hover:bg-red-50/40 transition-colors cursor-pointer"
                      onClick={() => toggle(i)}
                    >
                      <td className="px-3 py-2 font-medium text-slate-800">
                        <span className="inline-flex items-center gap-1.5" data-testid={`revision-toggle-${i}`}>
                          {open ? <ChevronDown className="h-4 w-4 text-red-500" /> : <ChevronRight className="h-4 w-4 text-red-400" />}
                          {r.cliente}
                        </span>
                      </td>
                      <td className={`${numCell} text-slate-700`}>{money(r.linea_total)}</td>
                      <td className={`${numCell} font-semibold text-slate-900`}>{money(r.valor_linea_usd)}</td>
                      <td className={`${numCell} text-slate-700`}>{money(r.total_cartera)}</td>
                      <td className={`${numCell} font-semibold text-red-600`}>{money(r.disponible_neto)}</td>
                    </tr>
                    {open && (
                      <tr key={`det-${i}`} data-testid={`revision-detail-${i}`} className="bg-slate-50/70 border-b border-[#f3d6d6]">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-2">
                            <CalendarClock className="h-4 w-4 text-slate-500" /> Detalle de vencimientos por año
                          </div>
                          {grupos.length === 0 ? (
                            <p className="text-xs text-slate-400">Este cliente no tiene fechas de vencimiento en el listado importado.</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {grupos.map(([anio, e]) => (
                                <div key={anio} className="rounded-lg border border-slate-200 bg-white px-3 py-2 min-w-[190px]">
                                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">{anio}</div>
                                  {e.Vencido > 0 && (
                                    <div className="flex items-center justify-between gap-4 text-[13px]">
                                      <span className="inline-flex items-center gap-1.5 text-red-700">
                                        <span className="h-2 w-2 rounded-full bg-red-500" /> Vencido
                                      </span>
                                      <span className="font-mono font-semibold text-red-700 tabular-nums">{money(e.Vencido)}</span>
                                    </div>
                                  )}
                                  {e["Por vencer"] > 0 && (
                                    <div className="flex items-center justify-between gap-4 text-[13px] mt-0.5">
                                      <span className="inline-flex items-center gap-1.5 text-emerald-700">
                                        <span className="h-2 w-2 rounded-full bg-emerald-500" /> Por vencer
                                      </span>
                                      <span className="font-mono font-semibold text-emerald-700 tabular-nums">{money(e["Por vencer"])}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
